import { useState } from 'react';
import { formatJMD } from '../../lib/finances';
import { lineTotal } from '../../lib/invoices';
import { editableLines } from '../../lib/stockist';
import ProductAutocomplete from './ProductAutocomplete';

const BLANK_ITEM = { description: '', product_id: null, unit_price: '', quantity: '' };

// Correcting a consignment after it's been sent.
//
// Different from the create form in one important way: some of it is no
// longer freely editable. Once a month's sales have been recorded against a
// line, those units are accounted for — the line can't be deleted and can't
// claim fewer were sent than have already sold. The form shows why rather
// than just disabling things.
//
// Changing a quantity moves your own stock by the difference, since the
// original send already took it off.
export default function StockistEditForm({ invoice, statements, products, onSubmit, onCancel }) {
  const [issueDate, setIssueDate] = useState(invoice.issue_date);
  const [notes, setNotes] = useState(invoice.notes || '');
  const [lines, setLines] = useState(() =>
    editableLines(invoice, statements).map((line) => ({
      id: line.id,
      description: line.description,
      product_id: line.productId,
      unit_price: String(line.unitPrice),
      quantity: String(line.quantity),
      minQuantity: line.minQuantity,
      locked: line.locked,
    }))
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const setLine = (index, patch) =>
    setLines((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const addRow = () => setLines((rows) => [...rows, { ...BLANK_ITEM, minQuantity: 0, locked: false }]);

  const removeRow = (index) =>
    setLines((rows) => rows.filter((_, i) => i !== index));

  const filled = lines.filter((l) => l.description.trim() && Number(l.quantity) > 0);
  const total = filled.reduce((sum, line) => sum + lineTotal(line), 0);

  const belowFloor = lines.filter(
    (line) => line.minQuantity > 0 && (Number(line.quantity) || 0) < line.minQuantity
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (filled.length === 0) {
      setError('A consignment needs at least one product.');
      return;
    }
    if (belowFloor.length > 0) {
      setError(
        `You can't send fewer than have already sold or come back: ${belowFloor
          .map((l) => `${l.description} (at least ${l.minQuantity})`)
          .join(', ')}.`
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(
        { issue_date: issueDate, notes: notes.trim() || null },
        filled.map((line) => ({
          id: line.id,
          description: line.description,
          product_id: line.product_id,
          unit_price: line.unit_price,
          quantity: line.quantity,
        }))
      );
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <form className="invoice-form" onSubmit={handleSubmit}>
      <div className="invoice-form-grid">
        <label className="field">
          <span>Stockist</span>
          <input value={invoice.bill_to_name} disabled />
        </label>
        <label className="field">
          <span>Date sent</span>
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </label>
        <label className="field">
          <span>Notes</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </label>
      </div>

      <div className="invoice-lines">
        <div className="stockist-lines-head">
          <span>Product</span>
          <span>Qty sent</span>
          <span>Unit price</span>
          <span>Value</span>
          <span />
        </div>

        {lines.map((line, index) => {
          const product = products.find((p) => p.id === line.product_id);
          const under = line.minQuantity > 0 && (Number(line.quantity) || 0) < line.minQuantity;
          return (
            <div className="stockist-line" key={line.id || `new-${index}`}>
              {line.locked ? (
                // The description is fixed once sales reference the line: it's
                // what the shop reported selling, and the statements name it.
                <span className="stockist-line-locked">{line.description}</span>
              ) : (
                <ProductAutocomplete
                  value={line.description}
                  products={products}
                  placeholder="Pick from your products"
                  onChange={(description) => setLine(index, { description, product_id: null })}
                  onPick={(picked) =>
                    setLine(index, {
                      description: picked.name,
                      product_id: picked.id,
                      unit_price: line.unit_price || (picked.price_jmd ?? ''),
                    })
                  }
                />
              )}

              <div className="stockist-line-qty">
                <input
                  type="number"
                  min={line.minQuantity || 0}
                  step="1"
                  value={line.quantity}
                  onChange={(e) => setLine(index, { quantity: e.target.value })}
                  className={under ? 'invalid' : undefined}
                  aria-label={`Quantity sent, line ${index + 1}`}
                />
                {line.minQuantity > 0 ? (
                  <em>{line.minQuantity} already accounted for</em>
                ) : (
                  product && <em>{product.stock_qty} in stock</em>
                )}
              </div>

              <input
                type="number"
                min="0"
                step="0.01"
                value={line.unit_price}
                onChange={(e) => setLine(index, { unit_price: e.target.value })}
                aria-label={`Unit price, line ${index + 1}`}
              />
              <span className="invoice-line-total">{formatJMD(lineTotal(line))}</span>
              {line.locked ? (
                <span className="stockist-line-lock" title="Has recorded sales — can't be removed">
                  🔒
                </span>
              ) : (
                <button
                  type="button"
                  className="invoice-line-remove"
                  onClick={() => removeRow(index)}
                  aria-label={`Remove line ${index + 1}`}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}

        <button type="button" className="ghost-button" onClick={addRow}>
          + Send another product
        </button>
      </div>

      <p className="dialog-note">
        Changing a quantity moves your own stock by the difference — the original send already took
        it off the shelf. Lines with recorded sales can't be removed, because the months they were
        reported in point at them.
      </p>

      <div className="invoice-form-footer">
        <div className="invoice-form-total">
          <span>Stock value sent</span>
          <strong>{formatJMD(total)}</strong>
        </div>
        <div className="invoice-form-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {error && <p className="po-error">{error}</p>}
    </form>
  );
}
